import {useEffect, useMemo} from '@wordpress/element';
import {Card} from './Card';
import {SourceLogo} from './SourceLogo';
import {useCompare} from '../../store/compare';
import {RatingDisplay} from "./RatingDisplay";
import {AddToCart} from "./AddToCart";
import {useMPE} from "../../hooks/useMPE";

export const ProductCardSkeleton = () => (
    <div
        className="flex flex-col animate-pulse w-full bg-white rounded-2xl shadow-sm border border-base-200 overflow-hidden">
        <div className="bg-base-300 w-full aspect-square"/>
        <div className="flex flex-col gap-2 p-4">
            <div className="h-3 bg-base-300 rounded w-1/4"/>
            <div className="h-4 bg-base-300 rounded w-3/4"/>
            <div className="h-3 bg-base-300 rounded w-full"/>
            <div className="h-3 bg-base-300 rounded w-2/3"/>
            <div className="h-3 bg-base-300 rounded w-1/4 mt-1"/>
            <div className="h-7 bg-base-300 rounded w-1/3 mt-2"/>
            <div className="h-8 bg-base-300 rounded-btn w-full mt-1"/>
            <div className="flex gap-2">
                <div className="h-8 bg-base-300 rounded-btn flex-1"/>
                <div className="h-8 bg-base-300 rounded-btn flex-1"/>
            </div>
        </div>
    </div>
);

export const ProductCard = ({product}) => {
    const {add, remove, contains} = useCompare();

    const raw = product ?? {};
    const sku = String(raw.sku ?? "");

    const brand = product.manufacturer;
    const name = product.name;
    const img = product.image;

    const sale = product.salePrice ?? null;
    const reg = product.regularPrice ?? null;

    const hasDiscount =
        sale != null &&
        reg != null &&
        Number(reg) > Number(sale);

    const discountPct = hasDiscount
        ? Math.round((1 - Number(sale) / Number(reg)) * 100)
        : 0;

    const rating = product.customerReviewAverage;
    const reviewCount = product.customerReviewCount ?? 0;

    const desc = product.description;

    const shortDesc = useMemo(() => {
        if (!desc) return null;
        const clean = desc
            .replace(/\n+/g, " ")
            .replace(/&[#A-Za-z0-9]+;/g, " ")
            .trim();
        return clean.length > 110 ? clean.slice(0, 107) + "…" : clean;
    }, [desc]);

    const detailsHref = `/product/${product.woocommerceSlug}`;
    const inCompare = contains(sku);
    const {initMpe} = useMPE();

    useEffect(() => {
        if (sale) {
            initMpe({
                mpeSelector: `#mpe-${sku}`,
                priceSelector: `#price-${sku}`,
                enableMoreInfoLink: false,
            })
        }
    }, [sale, initMpe])

    return (
        <Card
            href={detailsHref}
            imageSrc={img}
            className="text-base-content h-full border-none shadow-none hover:shadow-none"
            mediaSiblings={
                (hasDiscount || product.freeShipping) ? (
                    <div className="flex items-center gap-2">
                        {hasDiscount && (
                            <div
                                className="inline-flex items-center h-5 px-2 bg-[#FE7C22] text-white text-[9px] font-extrabold uppercase leading-none rounded-sm shadow-sm whitespace-nowrap">
                                {discountPct}% OFF
                            </div>
                        )}

                        {product.freeShipping && (
                            <div
                                className="inline-flex items-center h-5 px-2 bg-dark-blue text-white text-[9px] font-extrabold uppercase leading-none rounded-sm shadow-sm whitespace-nowrap">
                                FREE SHIPPING
                            </div>
                        )}
                    </div>
                ) : null
            }
            actions={
                <div className="flex flex-col w-full gap-2">
                    <AddToCart
                        sku={raw.woocommerceId}
                        className="btn-sm w-full border-none !text-[11px]"
                    />
                    <div className="flex gap-2">
                        <a
                            href={detailsHref}
                            className="btn btn-sm pt-btn-pill pt-btn-clear no-underline flex-1 !text-[11px]"
                            onClick={(e) => e.stopPropagation()}
                        >
                            View
                        </a>
                        <button
                            className={`btn btn-sm pt-btn-pill transition-all flex-1 !text-[11px] ${
                                inCompare ? 'btn-secondary' : 'btn-outline pt-orange border-current hover:bg-secondary/5'
                            }`}
                            onClick={(e) => {
                                e.preventDefault();
                                inCompare ? remove(sku) : add(product);
                            }}
                        >
                            {inCompare ? (
                                <>
                                    <i className="fa fa-check mr-1"/>
                                    Selected
                                </>
                            ) : (
                                "Compare"
                            )}
                        </button>
                    </div>
                </div>
            }
        >
            <div className="flex-1 flex flex-col">
                {brand && (
                    <div className="text-[10px] pt-orange font-black uppercase tracking-tighter mb-1">
                        {brand}
                    </div>
                )}

                <div
                    className="text-sm font-bold leading-tight text-base-content hover:text-primary transition-colors mb-2 line-clamp-2 min-h-[2rem]">
                    {name}
                </div>

                {shortDesc && (
                    <div className="text-[11px] text-base-content/60 mb-3 line-clamp-3 leading-relaxed min-h-[3rem]">
                        {shortDesc}
                    </div>
                )}

                <div className="flex items-center gap-1 mb-2">
                    <RatingDisplay name={`rating-${sku}`} value={rating} size="xs"/>
                    <span className="text-[10px] font-bold text-base-content/30 mt-0.5">
            ({reviewCount})
          </span>
                </div>

                <div className="mt-auto">
                    <div className="flex items-baseline gap-2">
                        {sale != null && (
                            <div id={`price-${sku}`} className="text-2xl font-black pt-dark-blue">
                                ${Number(sale).toFixed(2)}
                            </div>
                        )}
                        {hasDiscount && (
                            <div className="text-xs text-base-content/40 font-medium">
                                was <span className="line-through">${Number(reg).toFixed(2)}</span>
                            </div>
                        )}
                    </div>
                    <div id={`mpe-${sku}`} className="pt-legal !text-xs text-base-content/50 !p-0 leading-tight mt-0.5"></div>

                    <div className="mt-2">
                        <SourceLogo source={product?.source}/>
                    </div>
                </div>
            </div>
        </Card>
    );
};
